import { useEffect, useState } from 'react';
import { getSessionOnce } from './ory';
import { Session } from '@ory/client';
import * as Sentry from '@sentry/react';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { useUserStore } from 'src/auth/useUserStore';
import { Logger } from 'src/utils/Logger';

export type SessionData = Session & {
  identity: {
    metadata_public: {
      tenantId: string;
      userId: string;
      isAdmin?: boolean;
    };
  };
};

export const useSessionData = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<SessionData | null>(null);

  useEffect(() => {
    getSessionOnce()
      .then((session: SessionData) => {
        const validated = validateSessionMetadata(session);
        if (validated) {
          setupIdentity(validated);
        }
        setSession(validated);
        setIsLoading(false);
      })
      .catch(error => {
        const isUnauthorized =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error.response as { status: number })?.status === 401;

        if (!isUnauthorized) {
          Logger.error('An error occurred during the session check', error);
        }

        setIsLoading(false);
      });
  }, []);

  return { isLoading, session };
};

function validateSessionMetadata(session: SessionData): SessionData | null {
  const metadata = session.identity?.metadata_public;
  if (!metadata?.userId || !metadata?.tenantId) {
    return null;
  }
  return session;
}

function setupIdentity(session: SessionData) {
  const { userId, tenantId } = session.identity.metadata_public;
  const email = session.identity.traits.email;

  if (Mixpanel.getDistinctId() !== userId) {
    Mixpanel.identify(userId);
    Mixpanel.setPeople(email, tenantId);
  }

  const { userId: storedUserId, setUser } = useUserStore.getState();
  if (userId !== storedUserId) {
    Sentry.setUser({ id: userId, tenant: tenantId, email });
    const sentryAlertEmailAddress =
      email.includes('@afternoon.co.uk') || tenantId === 'afternoon' ? email : 'redacted';
    Sentry.setTags({ tenant: tenantId, email: sentryAlertEmailAddress });
    setUser(userId, tenantId);
  }
}
